import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, Wrench, Plus, ArrowLeft, Heart } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_META = {
  feature: { label: "New", icon: Sparkles, bg: "bg-[#3A5A4A]", text: "text-white" },
  improvement: { label: "Improved", icon: Plus, bg: "bg-[#E8DCC8]", text: "text-[#3A5A4A]" },
  fix: { label: "Fixed", icon: Wrench, bg: "bg-[#F3EFEA]", text: "text-[#7A736E]" },
};

function formatBody(body) {
  // Minimal markdown-like rendering: split paragraphs, keep "- " lines as bullets
  const blocks = [];
  let bullets = [];
  body.split(/\n+/).forEach((line, i) => {
    const t = line.trim();
    if (!t) return;
    if (t.startsWith("- ") || t.startsWith("* ")) {
      bullets.push(t.slice(2));
    } else {
      if (bullets.length) { blocks.push({ type: "ul", items: bullets }); bullets = []; }
      blocks.push({ type: "p", text: t });
    }
  });
  if (bullets.length) blocks.push({ type: "ul", items: bullets });
  return blocks;
}

export default function Changelog() {
  usePageMeta({
    title: "What's new — Hope Chest",
    description: "Release notes and updates for Hope Chest Digital Binder — new caregiving features, improvements, and fixes shipped each week.",
  });
  const [entries, setEntries] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/changelog`)
      .then((r) => setEntries(r.data))
      .catch(() => setEntries([]));
  }, []);

  return (
    <div className="min-h-screen bg-[#FCFAF8] relative overflow-hidden">
      <div className="hc-blob" style={{ background: "#E8DCC8", width: 420, height: 420, top: -120, right: -80 }} />
      <div className="hc-blob" style={{ background: "#D8E2D8", width: 360, height: 360, bottom: -100, left: -100 }} />

      <header className="relative z-10 max-w-3xl mx-auto px-6 pt-10 pb-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#7A736E] hover:text-[#3A5A4A] mb-8 transition" data-testid="changelog-back-home">
          <ArrowLeft className="w-4 h-4" /> Back to Hope Chest
        </Link>
        <p className="text-xs uppercase tracking-[0.2em] text-[#7A736E]">What&apos;s new</p>
        <h1 className="font-display text-4xl md:text-5xl font-semibold mt-2 mb-3" data-testid="changelog-title">
          Release notes
        </h1>
        <p className="text-[#7A736E] max-w-xl">
          We ship small improvements every week. Here&apos;s everything that&apos;s changed in Hope Chest, newest first.
        </p>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-6 pb-20">
        {entries === null && (
          <p className="text-[#7A736E]" data-testid="changelog-loading">Loading…</p>
        )}

        {entries !== null && entries.length === 0 && (
          <div className="hc-card p-10 text-center" data-testid="changelog-empty">
            <div className="w-14 h-14 rounded-full bg-[#F3EFEA] flex items-center justify-center mx-auto mb-3">
              <Heart className="w-6 h-6 text-[#C87B67]" fill="#C87B67" />
            </div>
            <h2 className="font-display text-xl mb-1">Nothing posted yet.</h2>
            <p className="text-sm text-[#7A736E]">Updates and new features will show up here soon.</p>
          </div>
        )}

        {entries !== null && entries.length > 0 && (
          <div className="space-y-8" data-testid="changelog-list">
            {entries.map((e) => {
              const cat = CATEGORY_META[e.category] || CATEGORY_META.feature;
              const Icon = cat.icon;
              const dateStr = e.published_at
                ? new Date(e.published_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
                : "";
              const blocks = formatBody(e.body || "");
              return (
                <article
                  key={e.id}
                  className="hc-card p-6 md:p-7 relative"
                  data-testid={`changelog-entry-${e.id}`}
                >
                  <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1.5 rounded-full ${cat.bg} ${cat.text} px-2.5 py-1 text-xs font-medium`}>
                        <Icon className="w-3 h-3" />
                        {cat.label}
                      </span>
                      {e.version && (
                        <span className="text-xs text-[#7A736E] font-mono bg-[#F3EFEA] px-2 py-0.5 rounded-full">
                          {e.version}
                        </span>
                      )}
                    </div>
                    <time className="text-xs text-[#7A736E] uppercase tracking-wider">{dateStr}</time>
                  </div>
                  <h2 className="font-display text-2xl mb-3 text-[#3D3935]">{e.title}</h2>
                  <div className="text-[#3D3935] leading-relaxed space-y-3 text-[15px]">
                    {blocks.map((b, i) => b.type === "p" ? (
                      <p key={i}>{b.text}</p>
                    ) : (
                      <ul key={i} className="list-disc pl-5 space-y-1.5 text-[#3D3935]">
                        {b.items.map((it, j) => <li key={j}>{it}</li>)}
                      </ul>
                    ))}
                  </div>
                </article>
              );
            })}
          </div>
        )}

        <div className="mt-16 hc-card p-6 bg-gradient-to-br from-[#F3EFEA] to-white border-[#E8DCC8] text-center">
          <p className="text-sm text-[#7A736E] mb-3">Have an idea or a request? We&apos;d love to hear it.</p>
          <Link
            to="/support"
            className="inline-flex items-center justify-center rounded-full bg-[#3A5A4A] hover:bg-[#2C4538] text-white px-6 py-2.5 text-sm font-medium transition"
            data-testid="changelog-request-feature"
          >
            Send us a feature request
          </Link>
        </div>
      </main>
    </div>
  );
}
